import React from 'react'
import {
  PiCalendarBlank,
  PiGauge,
  PiGridNine,
  PiTreePalm,
  PiUser,
  PiUsersThree,
  PiCirclesThreePlus,
  PiBell,
  PiMapPin,
  PiFiles,
  PiFileText,
  PiSunHorizon,
  PiGear,
  PiBuildings,
  PiPuzzlePiece,
  PiMicrosoftExcelLogo,
  PiCurrencyDollar,
  PiChartLine,
  PiChartBar
} from 'react-icons/pi'
import { allPlatforms } from '../../constants'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FrontendUrls } from '../../types/urls'

// Request Leave dropdown options (will be used in SidebarContent as a separate button)
export const requestLeaveOptions = [
  {
    path: '/app/leaves/request-leave',
    isLink: false,
    title: 'app.requestLeave',
    roles: ['Admin', 'User', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/leaves/add-leave',
    isLink: false,
    title: 'app.addLeave',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/request-toil',
    isLink: false,
    title: 'app.requestToil',
    roles: ['Admin', 'User', 'Approver'],
    plan: SubscriptionPlanEnum.complete,
    platform: allPlatforms,
    featureFlagName: FeatureFlagEnum.toil,
    ignorePlan: true,
  },
]

const menus = [
  // Direct Dashboard items (no section header)
  {
    path: '/app/dashboard',
    isLink: false,
    icon: <PiGauge />,
    title: 'sidebar.dashboard',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-dashboard-menu',
  }, {
    path: '/app/my-dashboard',
    isLink: false,
    icon: <PiGauge />,
    title: 'sidebar.dashboard',
    roles: ['User'],
    platform: allPlatforms,
    joyRideClass: 'joyride-dashboard-menu',
  }, {
    path: '/app/calendar',
    isLink: false,
    icon: <PiCalendarBlank />,
    title: 'app.calendar',
    roles: ['Admin', 'User', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-calendar-menu',
  }, {
    path: '/app/wallchart',
    isLink: false,
    icon: <PiGridNine />,
    title: 'app.wallchart',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-calendar-menu',
  },

  // TEAM & APPROVAL FLOWS SECTION
  {
    path: '__section__teamApprovalFlows',
    isSection: true,
    icon: <PiUsersThree />,
    title: 'sidebar.section.teamApprovalFlows',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/users',
    isLink: false,
    icon: <PiUser />,
    title: 'app.users',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-users-menu',
  },
  {
    path: FrontendUrls.departments,
    isLink: false,
    icon: <PiCirclesThreePlus />,
    title: 'app.departments',
    roles: ['Admin'],
    platform: allPlatforms,
    joyRideClass: 'joyride-teams-menu',
  },
  {
    path: '/app/notifications',
    isLink: false,
    icon: <PiBell />,
    title: 'sidebar.timeOffUpdates',
    roles: ['Admin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-notifications',
  },

  // LEAVE SETUP SECTION
  {
    path: '__section__leaveSetup',
    isSection: true,
    isCollapsible: true,
    icon: <PiTreePalm />,
    title: 'sidebar.section.leaveSetup',
    roles: ['Admin'],
    platform: allPlatforms,
    children: [
      {
        path: FrontendUrls.locations,
        isLink: false,
        icon: <PiMapPin />,
        title: 'app.locations',
        roles: ['Admin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-location-menu',
      },
      {
        path: FrontendUrls.leaveTypes,
        isLink: false,
        icon: <PiFiles />,
        title: 'app.leaveTypes',
        roles: ['Admin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-leave-types-menu',
      },
      {
        path: FrontendUrls.leavePolicies,
        isLink: false,
        icon: <PiFileText />,
        title: 'sidebar.leavePolicies',
        roles: ['Admin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-leave-policies-menu',
      },
    ],
  },

  // User-specific items for non-admins
  {
    path: '/app/holidays',
    isLink: false,
    icon: <PiSunHorizon />,
    title: 'app.holidays',
    roles: ['User', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-holidays-menu-user',
  }, {
    path: '/app/my-profile',
    isLink: false,
    icon: <PiUser />,
    title: 'app.myProfile',
    joyRideClass: 'joyride-my-profile-menu',
    roles: ['User', 'Approver'],
    platform: allPlatforms,
  },
  // APPROVERS ONLY SECTION
  // Addons only for approvers, because approvers can't see workspace settings
  {
    path: '__section__teamApprovalFlows',
    isSection: true,
    icon: <PiBuildings />,
    title: 'sidebar.section.workspaceSettings',
    roles: ['Approver'],
    platform: allPlatforms,
  },
  {
    path: FrontendUrls.addons,
    isLink: false,
    icon: <PiPuzzlePiece />,
    title: 'app.addons',
    roles: ['Approver'],
    tooltip: {
      tagTitle: '1 New',
      hideAfter: new Date('2024-08-31T00:00:00.000'),
    },
    platform: allPlatforms,
  },
  // END APPROVERS ONLY SECTION

  // ADMIN ONLY SECTION
  // WORKSPACE SETTINGS SECTION
  {
    path: '__section__workspaceSettings',
    isSection: true,
    isCollapsible: true,
    icon: <PiBuildings />,
    title: 'sidebar.section.workspaceSettings',
    roles: ['Admin'],
    platform: allPlatforms,
    children: [
      {
        path: '/app/settings/general',
        isLink: false,
        icon: <PiGear />,
        title: 'app.general',
        roles: ['Admin'],
        platform: allPlatforms,
      },
      // Addons only for admins, because approvers cant see workspace settings
      {
        path: FrontendUrls.addons,
        isLink: false,
        icon: <PiPuzzlePiece />,
        title: 'app.addons',
        roles: ['Admin'],
        tooltip: {
          tagTitle: '1 New',
          hideAfter: new Date('2024-08-31T00:00:00.000'),
        },
        platform: allPlatforms,
      },
      {
        path: '/app/imports',
        isLink: false,
        icon: <PiMicrosoftExcelLogo />,
        title: 'sidebar.bulkUploads',
        tooltip: {
          tagTitle: 'New',
          hideAfter: new Date('2025-02-02T00:00:00.000'),
        },
        roles: ['Admin'],
        joyRideClass: 'joyride-notifications',
        platform: allPlatforms,
      },
      {
        path: '/app/settings/billing',
        isLink: false,
        icon: <PiCurrencyDollar />,
        title: 'sidebar.billing',
        roles: ['Admin'],
        platform: allPlatforms,
      },
    ],
  },

  // REPORTS SECTION
  {
    path: '__section__reports',
    isSection: true,
    isCollapsible: true,
    icon: <PiChartLine />,
    title: 'sidebar.section.reports',
    roles: ['Admin'],
    platform: allPlatforms,
    children: [
      {
        path: FrontendUrls.reports,
        isLink: false,
        icon: <PiChartBar />,
        title: 'sidebar.reports',
        roles: ['Admin'],
        tooltip: {
          tagTitle: 'New',
          hideAfter: new Date('2025-02-02T00:00:00.000'),
        },
        platform: allPlatforms,
      },
    ],
  },
  // END ADMIN ONLY SECTION

  // RECHARGE TYPE - Rendered as custom card in SidebarContent, not as menu item
]

export default menus